import { Button, EButtonColorVariant, LinkButton, TextInput } from '@outdoorsyco/bonfire';
import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import { useMenuTriggerState } from 'react-stately';

import { useBreakpointContext } from '@/components/common/BreakpointContext';
import GuestsContent from '@/components/ui/SearchHeader/NavbarSearch/DesktopNavbarSearch/GuestsContent/GuestsContent';

import { FieldOverlay } from './FieldOverlay';
import { TGuestOccupancyConfig } from './types';

export type TGuestOccupancyValue = {
  adults: number;
  children: number;
  pet: boolean;
};

interface IGuestsFieldProps {
  config: TGuestOccupancyConfig;
  value: TGuestOccupancyValue;
  onChange: (value: TGuestOccupancyValue) => void;
}

// A single "Guests" trigger that opens the shared guests overlay (the navbar's
// GuestsContent, limited to adults + children + pet). Shows the total guest count;
// on mobile the sheet gets a Clear/Done footer.
export const GuestsField = ({ config, value, onChange }: IGuestsFieldProps) => {
  const intl = useIntl();
  const { isAboveTablet } = useBreakpointContext();

  const triggerRef = useRef<HTMLDivElement>(null);
  const triggerState = useMenuTriggerState({});

  const handleTriggerClick = (): void => triggerState.open();

  // The trigger is read-only, so typing does nothing — open the overlay on
  // Enter/Space (matching native button activation) for keyboard users.
  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleTriggerClick();
    }
  };

  const label = intl.formatMessage({
    id: '8ia6VX',
    defaultMessage: 'Guests',
    description: 'Locality guided search > Guests field label',
  });

  const guestCount = value.adults + value.children;
  const displayValue = intl.formatMessage(
    {
      id: 'ZOJnIe',
      defaultMessage: '{count, plural, one {# guest} other {# guests}}',
      description: 'Locality guided search > Guests field > Guest count summary',
    },
    { count: guestCount },
  );

  // Reset targets — the same defaults deserializeGuests seeds an untouched field with.
  const defaultAdults = config.adults?.defaultValue ?? config.adults?.minValue ?? 1;
  const defaultChildren = config.children?.defaultValue ?? 0;
  const isReset =
    value.adults === defaultAdults && value.children === defaultChildren && !value.pet;

  const handleClear = (): void =>
    onChange({ adults: defaultAdults, children: defaultChildren, pet: false });

  const guestsContent = (
    <GuestsContent
      open={triggerState.isOpen}
      // Adults + children (+ pet) only: no infants, no ADA toggle, and no navbar
      // search-filter analytics.
      isGuestOccupancyAvailable={false}
      showAccessible={false}
      disableAnalytics
      guests={{ adults: value.adults, children: value.children, pet_friendly: value.pet }}
      onChangeGuests={next =>
        onChange({
          adults: next.adults ?? value.adults,
          children: next.children ?? value.children,
          pet: !!next.pet_friendly,
        })
      }
      fullWidth={!isAboveTablet}
    />
  );

  return (
    <div ref={triggerRef}>
      <TextInput
        controlled
        readOnly
        label={label}
        ariaLabel={label}
        inputValue={displayValue}
        onChange={() => undefined}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        otherAriaProps={{ 'aria-haspopup': 'dialog', 'aria-expanded': triggerState.isOpen }}
      />

      <FieldOverlay
        triggerRef={triggerRef}
        triggerState={triggerState}
        content={guestsContent}
        // Footer (Clear / Done) is mobile-only; desktop closes on click-outside.
        footer={
          isAboveTablet ? undefined : (
            <div className="flex items-center justify-between w-full p-4">
              <LinkButton
                label={intl.formatMessage({ defaultMessage: 'Clear', id: '/GCoTA' })}
                onClick={handleClear}
                disabled={isReset}
              />
              <Button
                variant={EButtonColorVariant.Primary}
                label={intl.formatMessage({ defaultMessage: 'Done', id: 'JXdbo8' })}
                onClick={() => triggerState.close()}
              />
            </div>
          )
        }
      />
    </div>
  );
};
